import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useTokens } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio, getContrastTextColor } from '../utils/luminance';
import { GeometricPatternBackground } from '../utils/geometricPattern';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Explore Floor Plans CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'Explore Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

export function PropertyOverview() {
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);

  // Match Header `self_guided_tour_link` / PinnedCallouts dark CTA: white fill
  // + accent border/label at rest; secondary fill on hover. When primary is too
  // pale against the page background, fall back to the dark text token.
  const tokens = useTokens();
  const ctaAccent = useMemo(() => {
    const primary = tokens?.color?.primary;
    const background = tokens?.color?.background || '#ffffff';
    if (!primary) return 'var(--vibe-color-primary)';
    return contrastRatio(primary, background) >= 4.5
      ? 'var(--vibe-color-primary)'
      : 'var(--vibe-color-text)';
  }, [tokens]);
  const solidCtaOnSecondary = useMemo(() => {
    const secondary = tokens?.color?.secondary;
    return secondary ? getContrastTextColor(secondary) : 'var(--vibe-color-text-on-secondary)';
  }, [tokens]);

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();

  return (
    <section
      className="relative overflow-hidden pt-8 md:pt-12 pb-2 md:pb-3 bg-[var(--vibe-color-background)]"
      style={{
        ['--rise-overview-cta-accent' as string]: ctaAccent,
        ['--rise-overview-cta-on-secondary' as string]: solidCtaOnSecondary,
      }}
    >
      <GeometricPatternBackground />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div ref={headingRef} className="reveal-fade-up">
          <RichTextContent
            path="propertyoverview.pre_title"
            as="p"
            className="font-body text-sm sm:text-base text-[var(--vibe-color-text)] mb-6 tracking-wide uppercase"
          />

          <RichTextContent
            path="propertyoverview.title"
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold overview-title-gradient mb-4 leading-tight uppercase"
          />

          <div className="w-20 h-1 mx-auto mb-8 rounded-full overview-accent-bar" />

          <RichTextContent
            path="propertyoverview.description"
            as="div"
            className="font-body text-sm sm:text-base text-[var(--vibe-color-text)] max-w-3xl mx-auto leading-relaxed [&_a]:text-primary [&_a]:underline"
          />

          <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
            <a
              href={ctaLink.url || '/floor-plans'}
              target={ctaLink._target || '_self'}
              className="overview-cta overview-cta--solid group/cta relative inline-flex items-center justify-center gap-2 mt-8 px-10 py-4 font-body text-sm font-medium tracking-[0.2em] uppercase overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">
                {ctaLink.label || 'Explore Floor Plans'}
              </span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .overview-title-gradient {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .overview-accent-bar {
          background: linear-gradient(90deg, var(--vibe-color-secondary) 0%, color-mix(in srgb, var(--vibe-color-secondary) 75%, black) 100%);
        }

        /* Mirrors Header .nav-cta-btn--solid (self_guided_tour_link): white
           fill + accent border/label at rest; secondary fill on hover. */
        .overview-cta {
          border-radius: 2px;
          box-shadow: none;
        }
        .overview-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          transition: opacity 0.25s ease;
        }
        .overview-cta:hover {
          transform: translateY(-1px);
        }
        .overview-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
        .overview-cta--solid {
          background: var(--vibe-color-background, #fff);
          border: 1.5px solid var(--rise-overview-cta-accent, var(--vibe-color-primary));
          color: var(--rise-overview-cta-accent, var(--vibe-color-primary));
        }
        .overview-cta--solid::before {
          background: var(--vibe-color-secondary);
          opacity: 0;
        }
        .overview-cta--solid:hover {
          color: var(--rise-overview-cta-on-secondary, var(--vibe-color-text-on-secondary));
          border-color: var(--vibe-color-secondary);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 30%, transparent);
        }
        .overview-cta--solid:hover::before {
          opacity: 1;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: 'Life at our community is like nowhere else',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<span data-type="field-token" data-field="property_name"></span>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Discover the perfect blend of comfort, style, and convenience at our community.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.cta_link': ctaLinkDef,
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
